import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import {
    BarChart, Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer, // Ensure ResponsiveContainer is used for charts
} from "recharts";
import { useAuth } from "../../../hooks/useAuth";
import { getPunchDetails } from "../../../hooks/apiHandler"; 
import { fetchTimeandDateRange } from "../../../hooks/commanFunc";

// Utility to calculate shift duration in hours
const calculateDurationHours = (startISO, endISO) => {
    if (!startISO || !endISO) return 0;
    const start = new Date(startISO);
    const end = new Date(endISO);
    const durationMs = end.getTime() - start.getTime();
    return durationMs / (1000 * 60 * 60); // Convert milliseconds to hours
};

// Utility to format hours into Xh Ym
const formatHoursMinutes = (hoursFloat) => {
    if (hoursFloat === 0) return '0h 0m';
    const totalMinutes = Math.round(hoursFloat * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
};

// Utility to get employee role name from code
const getRoleName = (roleCode) => {
    switch (roleCode) {
        case 0: return 'Staff';
        case 1: return 'Manager';
        case 3: return 'Admin';
        default: return 'User';
    }
}


export const EmployeeShiftReport = ({ reportRange }) => {
    const theme = useTheme();
    const { Logout } = useAuth();
    const storeID = useRef(JSON.parse(window.localStorage.getItem("ds")));

    const [shiftChart, setShiftChart] = useState([]);
    const [shiftSummary, setShiftSummary] = useState([]);
    const [totalShifts, setTotalShifts] = useState(0);
    const [totalHours, setTotalHours] = useState(0);

    useEffect(() => {
        async function fetchShifts() {
            try {
                // 1. Determine Date Range
                const { start, end } = await fetchTimeandDateRange(reportRange.toLowerCase());
                
                // 2. Fetch Data
                const response = await getPunchDetails({
                    storeID: storeID.current,
                    startDate: start.toISOString(),
                    endDate: end.toISOString(),
                }, Logout);

                const data = response?.data;
                if (!data || !data.userTimelogs || !data.usersWithAccess) return;

                // --- DATA PROCESSING ---
                let totalShiftCount = 0;
                let totalShiftHours = 0;

                // 2a. Create User Lookup Map (for names and roles)
                const userAccessMap = data.usersWithAccess.reduce((acc, user) => {
                    acc[user.userID] = { name: user.name, role: getRoleName(user.role) };
                    return acc;
                }, {});

                // 2b. Aggregate Shifts and Calculate Totals
                const employeeTotals = {}; // Tracks {userID: {hours: 0, shifts: 0}}
                const dateWiseEmployeeHours = {}; // Tracks {date: {employeeName: hours}}
                
                data.userTimelogs.forEach(shift => {
                    const { userID, startTime, endTime } = shift;
                    const employee = userAccessMap[userID];
                    
                    if (!employee || !endTime) return; // Skip if user data missing or shift is incomplete

                    const durationHours = calculateDurationHours(startTime, endTime);
                    
                    if (!employeeTotals[userID]) {
                        employeeTotals[userID] = { name: employee.name, role: employee.role, totalHours: 0, shifts: 0 };
                    }
                    
                    // Sum totals
                    employeeTotals[userID].totalHours += durationHours;
                    employeeTotals[userID].shifts++;
                    totalShiftHours += durationHours;
                    totalShiftCount++;

                    // Aggregate hours by date for charting
                    const shiftDate = new Date(startTime).toISOString().split("T")[0]; // yyyy-mm-dd
                    if (!dateWiseEmployeeHours[shiftDate]) {
                        dateWiseEmployeeHours[shiftDate] = {};
                    }
                    dateWiseEmployeeHours[shiftDate][employee.name] = (dateWiseEmployeeHours[shiftDate][employee.name] || 0) + durationHours;
                });

                // 3. Prepare Final States
                
                // Summary (Table Data)
                const summaryData = Object.values(employeeTotals).map(e => ({
                    ...e,
                    totalHoursFormatted: formatHoursMinutes(e.totalHours),
                    totalHours: parseFloat(e.totalHours.toFixed(2))
                }));

                // Chart Data
                const chartData = Object.entries(dateWiseEmployeeHours).map(([date, users]) => {
                    const entry = { date };
                    Object.entries(users).forEach(([name, hours]) => {
                        entry[name] = parseFloat(hours.toFixed(2));
                    });
                    return entry;
                });
                
                // Determine all employee names for chart legends/bars
                const allEmployeeNames = Object.values(userAccessMap).map(u => u.name);


                setShiftChart(chartData);
                setShiftSummary(summaryData);
                setTotalShifts(totalShiftCount);
                setTotalHours(totalShiftHours.toFixed(1));
            } catch (err) {
                console.error("Failed to fetch employee shift data", err);
            }
        }

        fetchShifts();
    }, [reportRange, Logout]);

    // Use a fixed color palette for recharts
    const CHART_COLORS = ['#3f51b5', '#ff5722', '#43a047', '#9575cd', '#f06292', '#ffeb3b'];

    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            <Paper
                elevation={3}
                sx={{ p: 3, mb: 3, backgroundColor: "#fce4ec" }}
            >
                <Typography variant="h6" color="secondary" gutterBottom>
                    Employee Shift Hours by Date
                </Typography>
                <Box sx={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer>
                        <BarChart data={shiftChart} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis label={{ value: "Shift Hours", angle: -90, position: "insideLeft" }} />
                            <Tooltip formatter={(value) => `${value.toFixed(2)} hours`} />
                            <Legend />
                            {/* Dynamically render a Bar for every employee found */}
                            {shiftSummary.map((row, index) => (
                                <Bar
                                    key={row.name}
                                    dataKey={row.name}
                                    stackId="a" // Stack bars if required, otherwise remove stackId
                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>


            {/* 2. Summary Table */}
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#f3e5f5" }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                            Employee Shift Summary
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Employee Name</TableCell>
                                        <TableCell>Employee Role</TableCell>
                                        <TableCell align="right">Total No of Shifts</TableCell>
                                        <TableCell align="right">Total Hours</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {shiftSummary.map((row) => (
                                        <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell component="th" scope="row">{row.name}</TableCell>
                                            <TableCell>{row.role}</TableCell>
                                            <TableCell align="right">{row.shifts}</TableCell>
                                            <TableCell align="right">{row.totalHoursFormatted}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* 3. Footer Summary */}
            <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: "#e1f5fe" }}>
                <Typography variant="h6" color="info.main" gutterBottom>
                    Shift Totals
                </Typography>
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell>Total Shifts</TableCell>
                            <TableCell align="right">{totalShifts}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Estimated Total Hours</TableCell>
                            <TableCell align="right">{totalHours} hrs</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Paper>
        </Container>
    );
};